import { createContext, useContext, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { noop } from "lodash";
import { useFormContext } from "react-hook-form";
import { useFetchCheckoutData } from "@/react/hooks/paywalls/useFetchCheckoutData";
import { useGetPaywallsCurrencies } from "@/react/hooks/paywalls/useGetPaywallsCurrencies";
import { useIsPaywallCustomizationPhase2 } from "@/react/hooks/useIsPaywallCustomizationPhase2";
import type { Paywall, PaywallCurrencyCheckout } from "@/react/types";

export interface PaywallFormProviderProp {
  checkoutDataQuery?: ReturnType<typeof useFetchCheckoutData>["showQuery"];
  onClose: () => void;
  selectedCurrency?: PaywallCurrencyCheckout;
  supportedCurrenciesQuery?: ReturnType<typeof useGetPaywallsCurrencies>;
}

const PaywallFormProvider = createContext<PaywallFormProviderProp>({
  onClose: noop,
});
PaywallFormProvider.displayName = "PaywallFormProvider";

export const usePaywallFormContext = () => useContext(PaywallFormProvider);

export const PaywallFormProviderContext = ({
  children,
  onClose,
}: PropsWithChildren<PaywallFormProviderProp>) => {
  const isPaywallCustomizationPhase2 = useIsPaywallCustomizationPhase2();
  const supportedCurrenciesQuery = useGetPaywallsCurrencies();
  const { getValues } = useFormContext<Paywall>();
  const paywall = getValues();

  const selectedCurrency = useMemo<PaywallCurrencyCheckout | undefined>(
    () =>
      supportedCurrenciesQuery?.data?.find(
        currency => currency.id === Number(paywall.currency_id),
      ),
    [paywall?.currency_id, supportedCurrenciesQuery?.data],
  );

  const { showQuery: checkoutDataQuery } = useFetchCheckoutData(
    {
      preview_paywall: true,
      currency_code: selectedCurrency?.code,
    },
    isPaywallCustomizationPhase2 && !!selectedCurrency?.code,
  );

  const value = {
    supportedCurrenciesQuery,
    selectedCurrency,
    checkoutDataQuery,
    onClose,
  };

  return (
    <PaywallFormProvider.Provider value={value}>
      {children}
    </PaywallFormProvider.Provider>
  );
};
